import { m } from '@/locale/paraglide/messages';
import { useEffect, useRef, useState } from 'react';
import { trackRetentionEvent } from '@/analytics/retention-events';
import type { AuthPresentation } from '@/components/auth/auth-card';
import { resolveLoginCallbackUrl } from '@/components/auth/login-continuation';
import { DividerWithText } from '@/components/auth/divider-with-text';
import { FormError } from '@/components/shared/form-error';
import { Button } from '@/components/ui/button';
import { websiteConfig } from '@/config/website';
import { authClient } from '@/auth/client';
import { ensureAuthDeviceCookie } from '@/auth/client-auth-context';
import { prepareGoogleAdsTagForAuth } from '@/analytics/google-ads-signup';
import { getOAuthCallbackUrlWithAttribution } from '@/attribution/auth-callback';
import { DEFAULT_LOGIN_REDIRECT, Routes } from '@/lib/routes';
import { cn } from '@/lib/utils';
import { getPathWithLocale } from '@/lib/urls';
import { IconBrandGoogleFilled, IconLoader2 } from '@tabler/icons-react';
interface SocialLoginButtonProps {
  active?: boolean;
  callbackUrl?: string;
  onGoogleSignInStart?: () => void;
  presentation?: AuthPresentation;
  showDivider?: boolean;
}
export function SocialLoginButton({
  active = true,
  callbackUrl: propCallbackUrl,
  onGoogleSignInStart,
  presentation = 'page',
  showDivider = true,
}: SocialLoginButtonProps) {
  const paramCallbackUrl =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('callbackUrl')
      : null;
  const defaultCallbackUrl = getPathWithLocale(DEFAULT_LOGIN_REDIRECT);
  const callbackUrl = resolveLoginCallbackUrl({
    explicit: propCallbackUrl,
    fallback: defaultCallbackUrl,
    query: paramCallbackUrl,
  });
  const [isLoading, setIsLoading] = useState<'google' | null>(null);
  const [isSlow, setIsSlow] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const requestRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (active) return;
    requestRef.current?.abort();
    requestRef.current = null;
    setIsLoading(null);
    setIsSlow(false);
    setError(undefined);
  }, [active]);

  useEffect(
    () => () => {
      requestRef.current?.abort();
    },
    []
  );

  if (!websiteConfig.auth?.enableGoogleLogin) {
    return null;
  }
  const onClick = async (provider: 'google') => {
    if (requestRef.current) return;

    const controller = new AbortController();
    const startedAt = Date.now();
    let timedOut = false;
    let redirecting = false;
    requestRef.current = controller;
    setIsLoading(provider);
    setIsSlow(false);
    setError(undefined);
    trackRetentionEvent('google_oauth_start', {
      loginMethod: provider,
      path: window.location.pathname,
      presentation,
    });

    const slowTimer = window.setTimeout(() => {
      if (requestRef.current !== controller) return;
      setIsSlow(true);
      trackRetentionEvent('google_oauth_slow', {
        elapsedMs: Date.now() - startedAt,
        loginMethod: provider,
        path: window.location.pathname,
        presentation,
      });
    }, 4_000);
    const timeoutTimer = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 15_000);

    try {
      onGoogleSignInStart?.();
      ensureAuthDeviceCookie();
      prepareGoogleAdsTagForAuth();
      const attributedCallbackUrl =
        getOAuthCallbackUrlWithAttribution(callbackUrl);
      const result = await authClient.signIn.social(
        {
          provider,
          callbackURL: attributedCallbackUrl,
          disableRedirect: true,
          errorCallbackURL: getPathWithLocale(Routes.AuthError),
        },
        { signal: controller.signal }
      );
      if (result.error) {
        throw new Error('oauth_request_failed');
      }
      if (!result.data?.url) {
        throw new Error('oauth_redirect_missing');
      }

      redirecting = true;
      trackRetentionEvent('google_oauth_redirect', {
        elapsedMs: Date.now() - startedAt,
        loginMethod: provider,
        path: window.location.pathname,
        presentation,
      });
      window.location.assign(result.data.url);
    } catch (caught) {
      if (controller.signal.aborted && !timedOut) return;

      redirecting = false;
      const failureStage = timedOut
        ? 'timeout'
        : caught instanceof Error && caught.message === 'oauth_redirect_missing'
          ? 'invalid_response'
          : caught instanceof Error && caught.message === 'oauth_request_failed'
            ? 'request'
            : 'client';
      setError(
        timedOut
          ? m.auth_social_google_timeout()
          : m.auth_social_google_failed()
      );
      trackRetentionEvent('google_oauth_failure', {
        elapsedMs: Date.now() - startedAt,
        failureStage,
        loginMethod: provider,
        path: window.location.pathname,
        presentation,
      });
    } finally {
      window.clearTimeout(slowTimer);
      window.clearTimeout(timeoutTimer);
      if (requestRef.current === controller) {
        requestRef.current = null;
        if (!redirecting) {
          setIsLoading(null);
          setIsSlow(false);
        }
      }
    }
  };
  return (
    <div data-slot="auth-social-login" className="flex w-full flex-col gap-4">
      {showDivider && <DividerWithText text={m.auth_social_or()} />}
      <Button
        size="lg"
        className={cn(
          'w-full',
          presentation === 'modal' &&
            'h-11 bg-foreground text-background hover:bg-foreground/90'
        )}
        variant={presentation === 'modal' ? 'secondary' : 'outline'}
        onClick={() => onClick('google')}
        disabled={isLoading === 'google'}
      >
        {isLoading === 'google' ? (
          <IconLoader2 className="mr-2 size-4 animate-spin" />
        ) : (
          <IconBrandGoogleFilled
            className={cn(
              'mr-2 size-4',
              presentation === 'modal' && 'text-[#4285f4]'
            )}
          />
        )}
        <span>
          {isLoading === 'google'
            ? isSlow
              ? m.auth_social_google_slow()
              : m.auth_social_google_connecting()
            : m.auth_social_sign_in_with_google()}
        </span>
      </Button>
      <FormError message={error} />
    </div>
  );
}
